import type { PoolAwardHold, PoolHold, TeamSnapshot } from '@myrewards/contracts';
import { smallGroup } from '@myrewards/domain';
import { useId, useMemo, useRef, useState, type ReactElement, type RefObject } from 'react';
import {
  Input,
  Link,
  SearchField,
  ToggleButton,
  ToggleButtonGroup,
  type Key,
} from 'react-aria-components';
import { lastTwelveWords, shareWords } from '../insights/labels';
import { ROLE_LABEL } from '../session/presenter';
import { TwoColumns } from '../shell';
import {
  ButtonLink,
  Card,
  CardHelp,
  cx,
  EmptyArt,
  EmptyState,
  formatCount,
  formatMoney,
  formatMonth,
  HelpShownTag,
  Icon,
  plural,
  StatusPill,
  useHelpAnchor,
  useScrollRegion,
  type StatusTone,
} from '../ui';
import { LatestChange } from './ExecutivePanel';
import { STAGE } from './labels';
import type { SectorPanelModel } from './model';
import { MyEntriesCard } from './MyEntriesCard';
import { DelegateCard, StartAwardCard, YourDelegatesCard } from './SectorSideCards';
import s from './Home.module.css';

/** "Maritime Airborne" section: the pool card, the team snapshot and who's been recognized. */
export function SectorPanel({
  panel,
  insights,
  myName,
  meId,
  asOf,
  entriesRef,
}: {
  panel: SectorPanelModel;
  /** The person can open Insights (delegates can't: Level 1 hides it, so no links to it). */
  insights: boolean;
  /** The person's name, for a delegate's "by … for …" records. */
  myName: string;
  /** Who is reading, and Home's day: a pool's latest change (APP-148). */
  meId: string;
  asOf: string;
  /** A delegate's My entries heading, for the hero's "My entries" (D1). */
  entriesRef?: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  const headingId = useId();
  const team = panel.recognition.team;
  const delegate = panel.role === 'delegate';
  const role =
    delegate && panel.forPerson ? `Delegate · for ${panel.forPerson.name}` : ROLE_LABEL[panel.role];
  return (
    <section className={s.panel} aria-labelledby={headingId}>
      <header className={s.panelHead}>
        <h2 id={headingId} className={cx('disp', s.panelTitle)}>
          {panel.sector.name}
        </h2>
        <CardHelp
          topic={panel.sector.name}
          shows={
            delegate
              ? `What you need to enter awards for ${panel.forPerson?.name ?? 'the Sector Lead'}: the award types you can submit and your own entries.`
              : `Everything you look after for ${panel.sector.name}: this year’s pool, awards in progress, and who on the team has been recognized.`
          }
          canDo={
            insights && !delegate
              ? 'Recognize someone, open an award, or open Insights for the full picture.'
              : 'Start an award, or open one of your entries.'
          }
        />
        <StatusPill tone="info" size="sm" label={role} />
        {insights && !delegate && (
          <Link href="/insights" className={s.panelLink}>
            Open insights
            <Icon name="arrowRight" size={16} />
          </Link>
        )}
      </header>
      {panel.position !== null && (
        <PoolCard panel={panel} insights={insights} meId={meId} asOf={asOf} />
      )}
      {/* APP-112: delegates start awards and track their own entries; program choices stay on the form. */}
      {team === null ? (
        <TwoColumns
          sideLabel={`${panel.sector.name} · your delegation`}
          main={
            <>
              <StartAwardCard panel={panel} />
              <MyEntriesCard panel={panel} myName={myName} headingRef={entriesRef} />
            </>
          }
          side={
            <>
              <DelegateCard panel={panel} />
            </>
          }
        />
      ) : (
        <TwoColumns
          sideLabel={`${panel.sector.name} recognition`}
          main={<TeamSnapshotCard panel={panel} team={team} />}
          side={
            <>
              <MostRecognized panel={panel} team={team} />
              <YourDelegatesCard panel={panel} />
            </>
          }
        />
      )}
    </section>
  );
}

// --- Pool card -----------------------------------------------------------------------------------

function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.max(0, Math.min(100, (part / whole) * 100));
}

/**
 * A held award's state: a line that holds the pool isn't paid yet (APP-088), so the plain Paid
 * stage reads as where it is, with Payroll.
 */
function heldStage(stage: PoolAwardHold['stage']): { label: string; tone: StatusTone } {
  return stage === 'paid' || stage === 'closed'
    ? { label: 'With Payroll', tone: 'info' }
    : STAGE[stage];
}

/** One line of the pool's In progress list: an award by name, or a total that keeps it adding up. */
function HeldRow({ hold }: { hold: PoolHold }): ReactElement {
  if (hold.kind !== 'award') {
    // Spot awards never name who (APP-083); awards past the first 20 are the older ones (APP-088).
    return (
      <li className={s.heldRow}>
        <span className={s.heldText}>
          <span className={s.heldName}>
            {hold.kind === 'spot_total' ? 'Spot awards' : 'Older awards'}
          </span>
          <span className={s.meta}>{plural(hold.awards, 'award', 'awards')}</span>
        </span>
        <span className={s.heldAmount}>{formatMoney(hold.cashCents)}</span>
      </li>
    );
  }
  const stage = heldStage(hold.stage);
  return (
    <li className={s.heldRow}>
      <span className={s.heldText}>
        <Link href={`/awards/${hold.award.id}`} className={s.strongLink}>
          {hold.award.displayId}
        </Link>
        <span className={s.meta}>
          {plural(hold.people, 'person', 'people')}
          {hold.program ? ` · ${hold.program}` : ''}
        </span>
        <span className={s.heldStage}>
          {hold.waitingOnYou ? (
            <StatusPill tone="waiting" size="sm" label="Needs your OK" />
          ) : hold.overBudgetCents !== null ? (
            <StatusPill
              tone="problem"
              size="sm"
              label={`Over budget by ${formatMoney(hold.overBudgetCents)} · with the Executives`}
            />
          ) : (
            <StatusPill tone={stage.tone} size="sm" label={stage.label} />
          )}
        </span>
      </span>
      <span className={s.heldAmount}>{formatMoney(hold.cashCents)}</span>
    </li>
  );
}

function PoolCard({
  panel,
  insights,
  meId,
  asOf,
}: {
  panel: SectorPanelModel;
  insights: boolean;
  meId: string;
  asOf: string;
}): ReactElement {
  const delegate = panel.role === 'delegate';
  const scope = panel.sector.name;
  const year = String(panel.fiscalYear);
  const position = panel.position;
  const budget = position?.budget ?? null;
  const remaining = position?.remaining ?? null;
  const paid = position?.paid ?? { cashCents: 0, options: 0 };
  const inProgress = position?.inProgress ?? { cashCents: 0, options: 0 };
  const holds = panel.recognition.holds;
  // The list of awards shows only when it accounts for the whole amount in progress.
  const holdsTotal = holds.reduce((sum, h) => sum + h.cashCents, 0);
  const listComplete = holds.length > 0 && holdsTotal === inProgress.cashCents;
  const paidPct = budget ? percent(paid.cashCents, budget.cashCents) : 0;
  const heldPct = budget
    ? Math.min(100 - paidPct, percent(inProgress.cashCents, budget.cashCents))
    : 0;
  const awarded = paid.cashCents + inProgress.cashCents;
  const given = paid.options + inProgress.options;
  const anchor = useHelpAnchor('pool-card');
  return (
    <section className={s.poolCard} aria-label={`${scope} budget ${year}`} {...anchor.props}>
      <HelpShownTag shown={anchor.shown} placement="edge" />
      <div className={s.poolCol}>
        {budget && remaining ? (
          <>
            <p className={s.poolLabel}>
              <span className="eyebrow">{`${scope} · cash pool ${year}`}</span>
              <span className={s.poolOf}>of {formatMoney(budget.cashCents)}</span>
            </p>
            <p className={s.poolValue}>
              <span className={cx('disp', s.poolNumber, remaining.cashCents < 0 && s.overBudget)}>
                {/* "$2,913 over budget": the words say it, not a minus sign (QA-036). */}
                {formatMoney(Math.abs(remaining.cashCents))}
              </span>
              <span className={s.poolUnit}>{remaining.cashCents < 0 ? 'over budget' : 'left'}</span>
            </p>
            <div
              className={s.poolBar}
              role="img"
              aria-label={`${formatMoney(paid.cashCents)} paid, ${formatMoney(inProgress.cashCents)} in progress, ${formatMoney(remaining.cashCents)} left of ${formatMoney(budget.cashCents)}`}
            >
              <span className={s.barPaid} style={{ width: `${String(paidPct)}%` }} />
              <span className={s.barHeld} style={{ width: `${String(heldPct)}%` }} />
            </div>
            <p className={s.legend}>
              {/* The first day has nothing paid or in progress to tell apart: one line says so. */}
              {awarded > 0 && (
                <>
                  <span className={s.legendItem}>
                    <span className={cx(s.swatch, s.swatchPaid)} aria-hidden="true" />
                    {formatMoney(paid.cashCents)} paid
                  </span>
                  <span className={s.legendItem}>
                    <span className={cx(s.swatch, s.swatchHeld)} aria-hidden="true" />
                    {formatMoney(inProgress.cashCents)} in progress
                  </span>
                </>
              )}
              <span className={s.legendItem}>
                {awarded === 0
                  ? `$0 awarded · the full ${formatMoney(budget.cashCents)} is available for ${year}`
                  : `${formatMoney(awarded)} awarded`}
              </span>
            </p>
          </>
        ) : (
          <EmptyState
            variant="not-set"
            art="pool"
            size="sm"
            headline={`${year} budget not set yet`}
            body={`Waiting on the Executives to set the ${scope} pool. You can draft awards meanwhile.`}
            {...(panel.offers.length > 0 && {
              action: { label: 'Start a draft', href: `/recognize?sector=${panel.sector.id}` },
            })}
          />
        )}
      </div>
      <div className={s.poolCol}>
        {budget && remaining ? (
          <>
            <p className={s.poolLabel}>
              <span className="eyebrow">{`${scope} · options pool ${year}`}</span>
              <span className={s.poolOf}>of {formatCount(budget.options)}</span>
            </p>
            <p className={s.poolValue}>
              <span className={cx('disp', s.poolNumber)}>{formatCount(remaining.options)}</span>
              <span className={s.poolUnit}>left</span>
            </p>
            <div
              className={cx(s.poolBar, s.poolBarOptions)}
              role="img"
              aria-label={`${formatCount(given)} options given, ${formatCount(remaining.options)} left of ${formatCount(budget.options)}`}
            >
              <span
                className={s.barOptionsLeft}
                style={{ width: `${String(percent(remaining.options, budget.options))}%` }}
              />
            </div>
            <p className={s.legendNote}>
              {given === 0
                ? 'None granted yet'
                : `${formatCount(paid.options)} granted · ${formatCount(inProgress.options)} in progress`}{' '}
              · {formatCount(panel.recognition.minimumOptionsPerPerson)} minimum per person when
              options are given
            </p>
          </>
        ) : (
          <EmptyState
            variant="not-set"
            art="pool"
            size="sm"
            headline="Options budget not set yet"
            body="The Executives set it with the cash pool."
          />
        )}
      </div>
      <div className={s.poolCol}>
        <p className={s.poolLabel}>
          <span className="eyebrow">{`${scope} · in progress · ${formatMoney(inProgress.cashCents)}`}</span>
        </p>
        {inProgress.cashCents === 0 && inProgress.options === 0 ? (
          <div className={s.dashedEmpty}>
            <EmptyArt name="award" size="sm" />
            <p className={s.cardText}>
              {delegate
                ? `No awards in progress yet. Each award entered for ${panel.forPerson?.name ?? 'the Sector Lead'}, by you or them, shows here until Payroll pays it.`
                : 'No awards in progress yet. Each one you enter or approve shows here until Payroll pays it.'}
            </p>
          </div>
        ) : !listComplete ? (
          <div className={s.dashedEmpty}>
            <p className={s.cardText}>
              {formatMoney(inProgress.cashCents)}
              {inProgress.options > 0
                ? ` and ${plural(inProgress.options, 'option', 'options')}`
                : ''}{' '}
              held by awards that are submitted but not yet paid.
              {delegate && ' Yours are listed under My entries below.'}
            </p>
            {insights && (
              <Link href="/insights" className={s.strongLink}>
                See them in Insights
              </Link>
            )}
          </div>
        ) : (
          <ul className={s.heldList}>
            {holds.map((hold) => (
              <HeldRow key={hold.kind === 'award' ? hold.award.id : hold.kind} hold={hold} />
            ))}
          </ul>
        )}
      </div>
      {position !== null && (
        <LatestChange pool={position} meId={meId} asOf={asOf} className={s.poolLatest} />
      )}
    </section>
  );
}

// --- Team recognition snapshot -------------------------------------------------------------------

const INTENSITY_LABEL = ['none', 'a little', 'some', 'more', 'the most'] as const;
const HEAT = [s.heat0, s.heat1, s.heat2, s.heat3, s.heat4] as const;

type Sort = 'recent' | 'team';

function statusLabel(row: TeamSnapshot['rows'][number]): { text: string; tone?: string } {
  if (row.status === 'repeat') return { text: `Repeat · ${row.awards}`, tone: s.statusRepeat };
  if (row.status === 'recent') return { text: 'Recent', tone: s.statusRecent };
  if (row.status === 'earlier') return { text: plural(row.awards, 'award', 'awards') };
  return { text: 'None yet', tone: s.statusNone };
}

function TeamSnapshotCard({
  panel,
  team,
}: {
  panel: SectorPanelModel;
  team: TeamSnapshot;
}): ReactElement {
  // Day one is the sector's (nothing awarded yet), never the person's pending setup (QA-001).
  const nothingYet = panel.nothingYet;
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>(nothingYet ? 'team' : 'recent');
  const months = team.months;
  const first = months[0]?.month;
  const last = months[months.length - 1]?.month;
  const totalAwards = months.reduce((sum, m) => sum + m.awards, 0);
  const totalCash = months.reduce((sum, m) => sum + m.cashCents, 0);
  const maxAwards = Math.max(1, ...months.map((m) => m.awards));
  const nobody = team.recognized === 0;
  const heatRef = useRef<HTMLDivElement>(null);
  const heatRegion = useScrollRegion(heatRef, `${panel.sector.name} recognition by month`);

  const rows = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = team.rows.filter((row) => {
      const text = `${row.person.name} ${row.team}`.toLowerCase();
      return words.every((word) => text.includes(word));
    });
    return sort === 'team'
      ? [...filtered].sort(
          (a, b) => a.team.localeCompare(b.team) || a.person.name.localeCompare(b.person.name),
        )
      : filtered;
  }, [query, sort, team.rows]);

  const span =
    first && last
      ? `${formatMonth(first)} ${first.slice(0, 4)} – ${formatMonth(last)} ${last.slice(0, 4)}`
      : '12 months';
  // APP-199: the scope line says where the last 12 months start ("since Nov 1, 2025").
  const scope = first ? lastTwelveWords(`${first}-01`) : '12 months';

  return (
    <Card
      title="Team recognition snapshot"
      helpAnchor="team-snapshot"
      help={{
        shows:
          'Who in your sector has been recognized in the last 12 months. It counts awards paid or in progress; entries still waiting for approval aren’t counted yet.',
        canDo: `Search for a person or team, sort by team, or open the full team view. Repeat means ${team.repeatRule.awards}+ awards or ${formatMoney(team.repeatRule.cashCents)}+ in 12 months.`,
      }}
      aside={<span className={s.meta}>{`${panel.sector.name} · ${scope}`}</span>}
    >
      <div className={s.snapshotTop}>
        <dl className={s.kpis}>
          <div className={s.kpi}>
            <dt className="eyebrow">People</dt>
            <dd className={cx('disp', s.kpiValue)}>{formatCount(team.people)}</dd>
            <dd className={s.kpiNote}>
              {nothingYet ? 'from the directory' : plural(team.teams, 'team', 'teams')}
            </dd>
          </div>
          <div className={s.kpi}>
            <dt className="eyebrow">Recognized</dt>
            <dd className={cx('disp', s.kpiValue)}>{formatCount(team.recognized)}</dd>
            <dd className={s.kpiNote}>
              {nobody
                ? `of ${formatCount(team.people)} so far`
                : // APP-197: the counts, not a percent, for a sector of fewer than SMALL_GROUP people.
                  smallGroup(team.people)
                  ? `${shareWords(team.recognized, team.people, null)} in the sector`
                  : `${Math.round((team.recognized / Math.max(1, team.people)) * 100)}% of the sector`}
            </dd>
          </div>
          <div className={s.kpi}>
            <dt className="eyebrow">Repeat</dt>
            <dd className={cx('disp', s.kpiValue, team.repeat > 0 && s.kpiGold)}>
              {formatCount(team.repeat)}
            </dd>
            <dd className={s.kpiNote}>
              {team.repeatRule.awards}+ awards or {formatMoney(team.repeatRule.cashCents)}+
            </dd>
          </div>
          <div className={s.kpi}>
            <dt className="eyebrow">Not yet</dt>
            <dd className={cx('disp', s.kpiValue, !nobody && team.notYet > 0 && s.kpiRed)}>
              {formatCount(team.notYet)}
            </dd>
            <dd className={s.kpiNote}>{nobody ? 'everyone, for now' : 'no award in 12 months'}</dd>
          </div>
        </dl>
        <figure className={s.monthly}>
          <figcaption className={s.monthlyCaption}>
            <strong>Recognitions per month</strong>
            <span className={s.meta}>
              {nobody
                ? 'Starts with your first award'
                : `${formatCount(totalAwards)} in 12 months · ${formatMoney(totalCash)}`}
            </span>
          </figcaption>
          <div
            className={s.bars}
            role="img"
            aria-label={
              nobody
                ? 'No recognitions yet in the last 12 months'
                : months.map((m) => `${formatMonth(m.month)}: ${String(m.awards)}`).join(', ')
            }
          >
            {months.map((m, index) => (
              <span key={m.month} className={s.barSlot}>
                <span
                  className={cx(
                    s.bar,
                    nobody && s.barPlaceholder,
                    index === months.length - 1 && !nobody && s.barCurrent,
                  )}
                  style={{
                    height: nobody
                      ? `${String(18 + ((index * 7) % 5) * 6)}%`
                      : `${String(Math.max(6, (m.awards / maxAwards) * 100))}%`,
                  }}
                />
              </span>
            ))}
          </div>
          <div className={s.barMonths} aria-hidden="true">
            {months.map((m) => (
              <span key={m.month} className={s.barMonth}>
                {formatMonth(m.month)}
              </span>
            ))}
          </div>
        </figure>
      </div>

      <div className={s.snapshotTools}>
        <SearchField
          aria-label="Search name or team"
          value={query}
          onChange={setQuery}
          className={s.search}
        >
          <Icon name="search" size={16} className={s.searchIcon} />
          <Input className={s.searchInput} placeholder="Search name or team" />
        </SearchField>
        <ToggleButtonGroup
          aria-label="Sort the team"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[sort]}
          onSelectionChange={(keys: Set<Key>) => {
            const [key] = [...keys];
            if (key === 'recent' || key === 'team') setSort(key);
          }}
          className={s.segmented}
        >
          <ToggleButton id="recent" className={s.segment}>
            Most recent
          </ToggleButton>
          <ToggleButton id="team" className={s.segment}>
            By team
          </ToggleButton>
        </ToggleButtonGroup>
        <span className={s.heatKey} aria-hidden="true">
          Less
          {[1, 2, 3, 4].map((level) => (
            <span key={level} className={cx(s.heatCell, HEAT[level])} />
          ))}
          More $
        </span>
      </div>

      {nothingYet && (
        <p className={s.infoBanner}>
          <Icon name="info" size={16} />
          <span>
            <strong>Your first award will light up this map.</strong> All {formatCount(team.people)}{' '}
            people in {panel.sector.name} are here already, from the directory. Each month fills in
            as awards are paid or in progress.
          </span>
        </p>
      )}

      <div ref={heatRef} className={s.heatWrap} {...heatRegion}>
        <table className={s.heat}>
          <caption className="visually-hidden">
            {`Recognition by person and month, ${span}`}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={s.heatPerson}>
                {plural(team.people, 'person', 'people')}
              </th>
              {months.map((m) => (
                <th key={m.month} scope="col" className={s.heatMonth}>
                  {formatMonth(m.month)}
                </th>
              ))}
              <th scope="col" className={s.heatTotal}>
                12 months
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={14} className={s.heatEmpty}>
                  {query === ''
                    ? 'People show here as soon as the directory lists them.'
                    : `No one on this snapshot matches “${query}”. The full team view searches everyone.`}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const status = statusLabel(row);
                return (
                  <tr key={row.person.id}>
                    <th scope="row" className={s.heatPerson}>
                      <span className={s.heatName}>{row.person.name}</span>
                      <span className={s.heatTeam}>{row.team}</span>
                    </th>
                    {row.months.map((level, index) => (
                      <td key={months[index]?.month ?? index} className={s.heatTd}>
                        <span className={cx(s.heatCell, HEAT[level])} aria-hidden="true" />
                        <span className="visually-hidden">
                          {months[index] ? `${formatMonth(months[index].month)}: ` : ''}
                          {INTENSITY_LABEL[level] ?? 'none'}
                        </span>
                      </td>
                    ))}
                    <td className={cx(s.heatTotal, status.tone)}>{status.text}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={s.snapshotFoot}>
        <p className={s.meta}>
          {sort === 'team' ? 'Sorted by team' : 'Most recent award first'} · counts awards paid or
          in progress; entries waiting on approval are not counted.
        </p>
        <ButtonLink href="/insights/team" variant="ghost" size="sm">
          Open full team view
          <Icon name="arrowRight" size={16} />
        </ButtonLink>
      </div>
    </Card>
  );
}

// --- Most recognized and not yet -----------------------------------------------------------------

function MostRecognized({
  panel,
  team,
}: {
  panel: SectorPanelModel;
  team: TeamSnapshot;
}): ReactElement {
  const sample = team.notRecognized.sample;
  const more = team.notRecognized.count - sample.length;
  const biggest = team.notRecognized.biggestTeams;
  return (
    <>
      <Card
        title="Most recognized"
        titleSize="md"
        compact
        help={{
          shows: `The people in ${panel.sector.name} recognized most over 12 months, by cash. Repeat means ${team.repeatRule.awards}+ awards or ${formatMoney(team.repeatRule.cashCents)}+ in 12 months.`,
          canDo: 'Use it to spread recognition fairly across the team.',
        }}
      >
        <p className={s.meta}>
          {`${panel.sector.name} · ${
            team.months[0] === undefined
              ? '12 months'
              : lastTwelveWords(`${team.months[0].month}-01`)
          }`}
        </p>
        {team.mostRecognized.length === 0 ? (
          <EmptyState
            variant="first-day"
            art="people"
            size="sm"
            headline="No one recognized yet"
            body={`The people you recognize most over 12 months appear here, with a Repeat flag at ${team.repeatRule.awards}+ awards or ${formatMoney(team.repeatRule.cashCents)}+.`}
          />
        ) : (
          <ul className={s.plainList}>
            {team.mostRecognized.map((entry) => (
              <li key={entry.person.id} className={s.ranked}>
                <span className={s.rankAvatar} aria-hidden="true">
                  {entry.person.initials}
                </span>
                <span className={s.rankText}>
                  <span className={s.rankName}>{entry.person.name}</span>
                  <span className={s.meta}>
                    {plural(entry.awards, 'award', 'awards')} · {entry.team} · last in{' '}
                    {formatMonth(entry.lastMonth)}
                  </span>
                </span>
                <span className={s.rankAmount}>
                  {formatMoney(entry.cashCents)}
                  {entry.repeat && <StatusPill tone="waiting" size="sm" label="Repeat" />}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className={s.meta}>
          Repeat = {team.repeatRule.awards}+ awards or {formatMoney(team.repeatRule.cashCents)}+ in
          12 months.
        </p>
      </Card>
      <section className={s.notYet} aria-labelledby={`${panel.sector.id}-not-yet`}>
        <h2 id={`${panel.sector.id}-not-yet`} className={s.notYetTitle}>
          <span className={cx('disp', s.notYetCount)}>{formatCount(team.notRecognized.count)}</span>{' '}
          not recognized yet
        </h2>
        <p className={s.meta}>
          {team.recognized === 0
            ? `All ${plural(team.teams, 'team', 'teams')}, straight from the directory.`
            : biggest.length > 0
              ? `Most are in ${biggest.map((b) => `${b.team} (${b.count})`).join(' and ')}.`
              : 'Across the sector, over the last 12 months.'}
        </p>
        {sample.length > 0 && (
          <ul className={s.chips}>
            {sample.map((person) => (
              <li key={person.id} className={s.chip}>
                {person.name}
              </li>
            ))}
            {more > 0 && <li className={s.chipMore}>and {formatCount(more)} more</li>}
          </ul>
        )}
        <div className={s.notYetActions}>
          {panel.offers.length > 0 && (
            <ButtonLink href={`/recognize?sector=${panel.sector.id}`} variant="ghost" size="sm">
              Recognize
            </ButtonLink>
          )}
          <ButtonLink href="/insights/team?filter=not-yet" variant="ghost" size="sm">
            See all {formatCount(team.notRecognized.count)}
          </ButtonLink>
        </div>
      </section>
    </>
  );
}
